import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import { ColdIcon, WarmIcon, HotIcon } from "../../icons";
import DetailsSidebarCard from "./detailsSidebarCard";
import InteractionsSidebarCard from "./interactionsSidebarCard";
import { reminderDetail } from "../../../../pages/api/reminder_detail";

const SidebarTabsContents = ({
  showSidebar,
  setShowSidebar,
  leadID,
  leadPipelineStage,
  leadStatus,
  leadInterestLevel,
  leadName,
}) => {
  const [activeTab, setActiveTab] = useState("details");
  const [sendLead, setSendLead] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const {
    contactData,
    reminderData,
    interactions,
    interactionsCount,
    totalPages,
  } = reminderDetail({ leadID, page, pageSize });

  const navigate = useNavigate();

  const leadSourceLabels = {
    0: "Raw Lead",
    1: "Fresh Lead",
    2: "Profile Completed",
    3: "Meeting Completion",
    4: "Onboarded",
    5: "Activated",
    11: "Tanked",
  };
  const interestLevelLabels = {
    1: "Cold",
    2: "Warm",
    3: "Hot",
  };
  const getStatusStyle = (status) => {
    const styles = {
      0: "bg-gray-100 text-gray-600",
      1: "bg-[#FEFAD8] border-[#FEFAD8] text-black",
      2: "bg-[#BEC2FF] border-[#BEC2FF] text-[#262BC6]",
      3: "bg-[#E3F3FF] border-[#E3F3FF] text-[#00658E]",
      4: "bg-[#E7FFD6] border-[#E7FFD6] text-[#116D26]",
      5: "bg-[#E7FFD6] border-[#E7FFD6] text-[#116D26]",
      11: "bg-red-200 text-red-800",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };
  const getLeadStatusStyle = (status) => {
    const styles = {
      Active: "bg-[#FEFAD8] border-[#FEFAD8] text-black",
      Ongoing: "bg-[#FFEEDC] border-[#FFEEDC] text-[#A96900]",
      Inactive: "bg-[#EEEEEE] border-[#EEEEEE] text-[#757575]",
      Tanked: "bg-[#757575] border-[#757575] text-[#EEEEEE]",
    };
    return styles[status] || "bg-gray-100 text-gray-600";
  };

  const handleShowDetailsButton = () => {
    setShowSidebar((prev) => !prev);
    navigate(`/lead_journey/?lead_id=${leadID}`);
  };

  return (
    <>
      <div
        className={`flex fixed top-0 right-0 h-full w-[448px] bg-white shadow-lg z-50 transform transition-transform duration-300 ease-in-out
                ${showSidebar ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="px-4 py-3 flex-col border-b justify-between w-full">
          <div className="flex flex-row justify-between items-center">
            <button
              onClick={() => setShowSidebar(false)}
              className="w-[34px] h-[34px] gap-2 px-1 hover:text-gray-700 border rounded-full border-[#1E293B] text-[#1E293B]"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18"
                />
              </svg>
            </button>
            <button
              className="w-auto h-auto p-3 gap-2 rounded-full border border-solid p-2 hover:bg-[#E2E8F0] border-[#1E293B] text-[#1E293B] text-sm font-bold whitespace-nowrap"
              onClick={handleShowDetailsButton}
            >
              View Full Details
            </button>
          </div>
          <div className="py-4 px-2 flex flex-col justify-between ">
            <h3 className="text-sm font-bold text-[#94A3B8]">ID-{leadID}</h3>
            <h1 className="text-xl font-bold py-4">{leadName}</h1>
            <div className="flex flex-row gap-2 whitespace-nowrap">
              <span
                className={`w-auto h-[40px] py-2 px-4 gap-2 top-5 left-[22px] rounded-full border font-bold ${getStatusStyle(
                  leadPipelineStage
                )}`}
              >
                {leadSourceLabels[leadPipelineStage] || "Unknown"}
              </span>
              <span
                className={`w-auto h-[40px] py-2 px-4 p gap-2 top-5 left-[22px] rounded-full border font-bold ${getLeadStatusStyle(
                  leadStatus
                )}`}
              >
                {leadStatus}
              </span>
              <span className="w-auto h-[40px] py-2 px-4 gap-2 top-5 left-[22px] flex rounded-full border font-bold bg-[#EEEEEE] border-[#EEEEEE] text-[#1E293B]">
                {interestLevelLabels[leadInterestLevel] || "Unknown"}
                {leadInterestLevel === 1 && (
                  <img
                    src={ColdIcon}
                    alt="Cold"
                    className="w-6 h-6 inline-block gap-1"
                  />
                )}
                {leadInterestLevel === 2 && (
                  <img
                    src={WarmIcon}
                    alt="Warm"
                    className="w-6 h-6 inline-block gap-1"
                  />
                )}
                {leadInterestLevel === 3 && (
                  <img
                    src={HotIcon}
                    alt="Hot"
                    className="w-6 h-6 inline-block gap-1"
                  />
                )}
              </span>
            </div>
            <div className="pt-4 flex justify-between border-b items-center border-gray-200 overflow-y-hidden scrollbar-hide cursor-pointer">
              <a
                onClick={() => setActiveTab("details")}
                className={`flex-1 text-center font-bold pb-2 -mb-[1px] ${
                  activeTab === "details"
                    ? "border-b-4 border-[#F7E21E] text-[#1E293B]"
                    : "text-[#94A3B8]"
                }`}
              >
                Details
              </a>
              <a
                onClick={() => setActiveTab("interaction")}
                className={`flex-1 text-center font-bold pb-2 -mb-[1px] ${
                  activeTab === "interaction"
                    ? "border-b-4 border-[#F7E21E] text-[#1E293B]"
                    : "text-[#94A3B8]"
                }`}
              >
                Interaction History
              </a>
            </div>
            <div className="py-4">
              {activeTab === "details" && (
                <div>
                  <DetailsSidebarCard
                    contactData={contactData}
                    reminderData={reminderData}
                  />
                </div>
              )}
              {activeTab === "interaction" && (
                <div className="overflow-y-auto max-h-[60vh]">
                  <InteractionsSidebarCard
                    interactions={interactions}
                    page={page}
                    pageSize={pageSize}
                    interactionsCount={interactionsCount}
                    totalPages={totalPages}
                    setPage={setPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        onClick={() => setShowSidebar(false)}
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out z-40 ${
          showSidebar ? "opacity-30 visible" : "opacity-0 invisible"
        }`}
      />
    </>
  );
};
export default SidebarTabsContents;
